import { useState, useEffect } from "react";
import { NavLink, useLocation, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home,
  Calendar,
  FileText,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Users,
  Building2,
  UserPlus,
  LayoutDashboard,
  Briefcase,
  Package,
  UserCog,
  X,
  Ticket,
  Shield,
  DollarSign,
  HelpCircle,
  CreditCard,
  Video,
  TrendingUp,
  GitBranch,
  Bell,
  Activity
} from "lucide-react";
import { IntegrationHealthBadge } from "@/components/layout/IntegrationHealthBadge";
import { cn } from "@/lib/utils";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { Button } from "@/components/ui/button";
import { useRole, isAdmin, isClient, isTeam } from "@/contexts/RoleContext";
import { getPermissions } from "@/lib/permissions";
import { useTeamPermissions, type TeamPermissionKey } from "@/lib/team-permissions";
import ventureHillsLogo from "@/assets/venture-hills-logo.png";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";

// Full admin nav items (for super_admin and team_admin)
const fullAdminNavItems = [
  { icon: LayoutDashboard, label: "Command Center", path: "/admin/command-center" },
  { icon: Building2, label: "Venture Hills Overview", path: "/admin/overview" },
  { icon: UserPlus, label: "Onboarding Process", path: "/admin/onboarding" },
  { icon: Briefcase, label: "Pipeline", path: "/admin/pipeline" },
  { icon: LayoutDashboard, label: "Dashboards", path: "/admin/dashboards" },
  { icon: GitBranch, label: "Attribution", path: "/admin/attribution" },
  { icon: UserCog, label: "Sales Operations", path: "/admin/sales-ops" },
  { icon: Calendar, label: "Demos", path: "/admin/demos-downloads" },
  { icon: Video, label: "Demo Library", path: "/admin/demo-library" },
  { icon: Package, label: "Packages", path: "/admin/packages" },
  { icon: FileText, label: "Reports", path: "/admin/reports" },
  { icon: Activity, label: "Integration Health", path: "/admin/integration-health" },
  { icon: Ticket, label: "Support Tickets", path: "/admin/support-tickets" },
  { icon: Shield, label: "Roles & Permissions", path: "/admin/roles-permissions" },
  { icon: Bell, label: "Notification Settings", path: "/admin/notification-settings" },
];

// Limited nav items for member role
const memberNavItems = [
  { icon: LayoutDashboard, label: "Command Center", path: "/admin/command-center" },
  { icon: UserPlus, label: "Onboarding Process", path: "/admin/onboarding" },
  { icon: Ticket, label: "Support Tickets", path: "/admin/support-tickets" },
  { icon: LayoutDashboard, label: "Client Performance", path: "/admin/dashboards" },
];

// Base client nav items
const getClientNavItems = (services: { demos: boolean }) => {
  const items = [
    { icon: Home, label: "Dashboard", path: "/dashboard" },
  ];

  if (services.demos) {
    items.push({ icon: Calendar, label: "Demos", path: "/demos" });
  }
  
  // Demo Library (recordings & outcomes)
  items.push({ icon: Video, label: "Demo Library", path: "/demo-library" });
  
  // Marketing (live tracking)
  items.push({ icon: TrendingUp, label: "Marketing", path: "/marketing" });

  // Attribution
  items.push({ icon: GitBranch, label: "Attribution", path: "/attribution" });
  
  // Sales Revenue (all packages)
  items.push({ icon: DollarSign, label: "Sales & Revenue", path: "/sales-revenue" });
  
  // Package Details
  items.push({ icon: Package, label: "Package Details", path: "/package-details" });
  
  // Billing
  items.push({ icon: CreditCard, label: "Billing", path: "/billing" });
  
  // Reports
  items.push({ icon: FileText, label: "Reports", path: "/reports" });
  
  // Support
  items.push({ icon: HelpCircle, label: "Support", path: "/support" });
  
  // Settings
  items.push({ icon: Settings, label: "Settings", path: "/settings" });
  
  return items;
};

// Elite-only items (closers management)
const eliteClientNavItems = [
  { icon: Users, label: "Sales Team", path: "/sales" },
];

// Team (setter / closer) nav items — each gated by isAllowed(permission_key)
const setterNavItems: Array<{ icon: any; label: string; path: string; key: TeamPermissionKey }> = [
  { icon: Home, label: "Home", path: "/setter/home", key: "setter.home" },
  { icon: Users, label: "Leads", path: "/setter/leads", key: "setter.leads" },
  { icon: Settings, label: "Settings", path: "/setter/settings", key: "setter.settings" },
];

const closerNavItems: Array<{ icon: any; label: string; path: string; key: TeamPermissionKey }> = [
  { icon: Home, label: "Home", path: "/closer/home", key: "closer.home" },
  { icon: Video, label: "Fathom", path: "/closer/fathom", key: "closer.fathom" },
  { icon: Video, label: "Demo Library", path: "/closer/demo-library", key: "closer.demo_library" },
  { icon: Settings, label: "Settings", path: "/closer/settings", key: "closer.settings" },
];

interface AppSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function AppSidebar({ collapsed, onToggle, mobileOpen, onMobileClose }: AppSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { role, adminSubRole, clientPackage, clientId } = useRole();
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const [clientData, setClientData] = useState<{
    company_name: string;
    logo_url: string | null;
    contact_name: string | null;
  } | null>(null);
  const [adminName, setAdminName] = useState<string>('');

  // Get admin sub-role for permission checks (from session via RoleContext, not localStorage)
  const adminRole = adminSubRole ?? 'member';
  const permissions = getPermissions(adminRole);

  // Team-role permissions (setter/closer). userId fetched lazily by the hook.
  const [teamUserId, setTeamUserId] = useState<string | null>(null);
  useEffect(() => {
    if (role === 'setter' || role === 'closer') {
      supabase.auth.getUser().then(({ data }) => setTeamUserId(data?.user?.id ?? null));
    }
  }, [role]);
  const { data: teamPerms } = useTeamPermissions(teamUserId, role);
  const isAllowed = (k: TeamPermissionKey) => teamPerms?.isAllowed(k) ?? false;

  // Fetch user data for display
  useEffect(() => {
    const fetchUserData = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      if (isAdmin(role) || isTeam(role)) {
        // Admins AND team members (setter/closer) live in admin_users — both resolve their
        // display name the same way. Previously team users hit the `else` branch and looked
        // up a nonexistent clients row, rendering "Client User" as the profile name.
        const { data: admin } = await supabase
          .from('admin_users')
          .select('full_name')
          .eq('email', user.email)
          .single();
        if (admin?.full_name) setAdminName(admin.full_name);
      } else if (clientId) {
        const { data } = await supabase
          .from('clients')
          .select('company_name, logo_url, contact_name')
          .eq('id', clientId)
          .single();
        if (data) setClientData(data);
      }
    };

    fetchUserData();
  }, [role, clientId]);
  
  // Get client services from localStorage (default to demos only for standard)
  const clientServices = (() => {
    try {
      const stored = localStorage.getItem('client_services');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {}
    return { demos: true };
  })();
  
  // Determine navigation items based on role
  const getNavItems = () => {
    if (isAdmin(role)) {
      // Check if member role (limited navigation)
      if (!permissions.show_all_navigation) {
        return memberNavItems;
      }
      return fullAdminNavItems;
    } else if (role === 'setter') {
      return setterNavItems
        .filter((i) => isAllowed(i.key))
        .map(({ icon, label, path }) => ({ icon, label, path }));
    } else if (role === 'closer') {
      return closerNavItems
        .filter((i) => isAllowed(i.key))
        .map(({ icon, label, path }) => ({ icon, label, path }));
    } else {
      // Client navigation
      const baseItems = getClientNavItems(clientServices);
      // Insert elite items after Demo Library if elite package
      if (clientPackage === 'elite') {
        const insertIndex = 4; // After Demo Library
        return [
          ...baseItems.slice(0, insertIndex),
          ...eliteClientNavItems,
          ...baseItems.slice(insertIndex)
        ];
      }
      return baseItems;
    }
  };
  
  const navItems = getNavItems();

  const handleSignOut = async () => {
    // Invalidate the Supabase JWT session first (SEC-04)
    await supabase.auth.signOut();
    // Clear all auth-related localStorage (onAuthStateChange handler also does this,
    // but clear explicitly for immediate effect)
    localStorage.removeItem('onboarding_complete');
    localStorage.removeItem('impersonating_client');
    localStorage.removeItem('user_role');
    localStorage.removeItem('admin_user');
    localStorage.removeItem('account_type');
    localStorage.removeItem('active_client_id');
    localStorage.removeItem('client_package');
    localStorage.removeItem('client_services');
    sessionStorage.clear();
    navigate('/login');
  };

  const sidebarContent = (
    <>
      {/* Logo - Client Branding or Venture Hills */}
      <div className="p-4 border-b border-sidebar-border">
        {isClient(role) && clientData ? (
          <Link to="/dashboard" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            {clientData.logo_url ? (
              <img 
                src={clientData.logo_url} 
                alt={clientData.company_name} 
                className="w-10 h-10 flex-shrink-0 object-contain rounded-lg"
              />
            ) : (
              <div className="w-10 h-10 flex-shrink-0 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <span className="text-lg font-bold text-primary-foreground">
                  {clientData.company_name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            {!collapsed && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col"
              >
                <span className="font-bold text-sm text-foreground truncate max-w-[140px]">
                  {clientData.company_name}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  Powered by Venture Hills
                </span>
              </motion.div>
            )}
          </Link>
        ) : (
          <Link to={isAdmin(role) ? "/admin/command-center" : role === 'setter' ? "/setter/home" : role === 'closer' ? "/closer/home" : "/dashboard"} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <img 
              src={ventureHillsLogo} 
              alt="Venture Hills" 
              className="w-10 h-10 flex-shrink-0 object-contain"
            />
            {!collapsed && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="font-bold text-lg bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"
              >
                Venture Hills
              </motion.span>
            )}
          </Link>
        )}
      </div>

      {/* Package Badge (Client Only) */}
      {isClient(role) && !collapsed && (
        <div className="px-4 py-2 border-b border-sidebar-border">
          <BadgeVenture variant={clientPackage === 'elite' ? 'info' : 'default'}>
            {clientPackage === 'elite' ? 'Elite Package' : 'Standard Package'}
          </BadgeVenture>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path || 
            (item.path !== "/dashboard" && item.path !== "/admin/command-center" && location.pathname.startsWith(item.path));
          
          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onMobileClose}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                "hover:bg-sidebar-accent group",
                isActive && "bg-sidebar-accent border-l-2 border-l-secondary"
              )}
            >
              <item.icon 
                className={cn(
                  "w-5 h-5 flex-shrink-0 transition-colors",
                  isActive ? "text-secondary" : "text-sidebar-foreground group-hover:text-sidebar-accent-foreground"
                )} 
              />
              {!collapsed && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={cn(
                    "text-sm font-medium transition-colors",
                    isActive ? "text-foreground" : "text-sidebar-foreground group-hover:text-sidebar-accent-foreground"
                  )}
                >
                  {item.label}
                </motion.span>
              )}
              {!collapsed && item.path === "/admin/integration-health" && <IntegrationHealthBadge />}
            </NavLink>
          );
        })}
      </nav>

      {/* User Profile */}
      <div className="p-3 border-t border-sidebar-border">
        <div className={cn(
          "flex items-center gap-3 p-3 rounded-lg bg-sidebar-accent",
          collapsed && "justify-center"
        )}>
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-bold text-primary-foreground">
              {isAdmin(role) || isTeam(role)
                ? (adminName ? adminName.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2) : 'VH')
                : (clientData?.contact_name?.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2) || 'C')}
            </span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {isAdmin(role)
                  ? (adminName || 'Admin User')
                  : isTeam(role)
                  ? (adminName || (role === 'setter' ? 'Setter' : 'Closer'))
                  : (clientData?.contact_name || 'Client User')}
              </p>
              <BadgeVenture
                variant={isAdmin(role) ? 'warning' : isTeam(role) ? 'info' : 'info'}
                className="mt-1"
              >
                {isAdmin(role)
                  ? (adminRole === 'super_admin' ? 'Super Admin' : adminRole === 'team_admin' ? 'Team Admin' : 'Member')
                  : role === 'setter'
                  ? 'Setter'
                  : role === 'closer'
                  ? 'Closer'
                  : clientPackage === 'elite' ? 'Elite' : 'Standard'}
              </BadgeVenture>
            </div>
          )}
        </div>
        
        <button 
          onClick={() => setShowSignOutDialog(true)}
          className={cn(
            "flex items-center gap-3 w-full px-3 py-2 mt-2 rounded-lg transition-colors",
            "text-muted-foreground hover:text-foreground hover:bg-sidebar-accent",
            collapsed && "justify-center"
          )}
        >
          <LogOut className="w-4 h-4" />
          {!collapsed && <span className="text-sm">Sign Out</span>}
        </button>
      </div>

      {/* Sign Out Confirmation Dialog */}
      <AlertDialog open={showSignOutDialog} onOpenChange={setShowSignOutDialog}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <LogOut className="w-5 h-5 text-muted-foreground" />
              Sign Out
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to sign out? You will need to log in again to access your account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSignOut}>
              Sign Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Collapse Toggle - Desktop only */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-sidebar-accent border border-sidebar-border flex items-center justify-center hover:bg-surface-elevated transition-colors hidden md:flex"
      >
        {collapsed ? (
          <ChevronRight className="w-3 h-3" />
        ) : (
          <ChevronLeft className="w-3 h-3" />
        )}
      </button>
    </>
  );
  
  return (
    <>
      {/* Desktop Sidebar */}
      <motion.aside
        className="fixed left-0 top-0 h-full bg-sidebar border-r border-sidebar-border flex-col z-50 hidden md:flex"
        initial={false}
        animate={{ width: collapsed ? 72 : 240 }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
      >
        {sidebarContent}
      </motion.aside>

      {/* Mobile Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 md:hidden"
              onClick={onMobileClose}
            />
            <motion.aside
              initial={{ x: -240 }}
              animate={{ x: 0 }}
              exit={{ x: -240 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="fixed left-0 top-0 h-full w-60 bg-sidebar border-r border-sidebar-border flex flex-col z-50 md:hidden"
            >
              {/* Mobile Close Button */}
              <button
                onClick={onMobileClose}
                className="absolute top-4 right-4 p-2 rounded-lg hover:bg-sidebar-accent transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              {sidebarContent}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
